import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { List, ListItem } from 'chaoskit/src/components'
import { Highlight, connectMenu } from 'react-instantsearch-dom'
import { link } from 'chaoskit/src/assets/styles/utility'
import { StylesAvatarVariables } from 'chaoskit/src/components/Avatar'

import TutorialTeaser, {
  getTutorialCategory,
} from '../community/tutorials/TutorialTeaser'

import { StylesAlgoliaVariables } from './algolia-global'

import useAuthor, { getAuthor } from '~hooks/useAuthor'
import Image from '~components/Image'

const CategoryMenu = ({ items, refine, createURL }) => {
  const theme = useTheme()

  return (
    <List space="base">
      {items.length > 0 ? (
        items.map((item) => {
          const categoryMeta = getTutorialCategory([item.label])

          return (
            <ListItem key={item.value}>
              <a
                href={createURL(item.value)}
                onClick={(event) => {
                  event.preventDefault()

                  refine(item.value)
                }}
                css={[
                  link.reset(theme),
                  {
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr',
                    gap: theme.space.small,
                    alignItems: 'center',
                    position: 'relative',

                    '&:hover, &:focus': {
                      '.G__Algolia__Category__IconWrapper': {
                        transform: 'scale(1.05)',
                      },
                    },
                  },

                  item.isRefined && {
                    '&::after': {
                      content: "''",
                      width: StylesAlgoliaVariables.notificationSize,
                      height: StylesAlgoliaVariables.notificationSize,
                      position: 'absolute',
                      background: theme.color.info.base,
                      borderRadius: theme.borderRadius.rounded,
                      zIndex: 2,
                      top: 0,
                      left: -StylesAlgoliaVariables.notificationSize / 4,
                      border: `2px solid ${theme.color.light.base}`,
                    },
                  },
                ]}
              >
                <div
                  css={{
                    background: theme.fontColor.base,
                    borderRadius: theme.borderRadius.rounded,
                    position: 'relative',
                    width: StylesAvatarVariables(theme).size.base,
                    height: StylesAvatarVariables(theme).size.base,
                    transition: `transform ${theme.timing.base} ${theme.transition.bounce}`,
                    transformOrigin: 'center center',
                  }}
                  className="G__Algolia__Category__IconWrapper"
                >
                  <Image
                    css={{
                      width: StylesAvatarVariables(theme).size.base * 0.65,
                      height: StylesAvatarVariables(theme).size.base * 0.65,
                      filter: 'brightness(0) invert(1)',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                    }}
                    src={categoryMeta.icon}
                    alt={item.label}
                  />
                </div>
                <div>
                  <div
                    css={[
                      {
                        ...theme.text.small,
                        fontWeight: theme.fontWeight.bold,
                      },
                    ]}
                  >
                    {item.label}
                  </div>
                  <div
                    css={{
                      ...theme.text.xsmall,
                      marginTop: theme.space.xsmall,
                      color: theme.fontColor.muted,
                    }}
                  >
                    {item.count} article{item.count > 1 && 's'}
                  </div>
                </div>
              </a>
            </ListItem>
          )
        })
      ) : (
        <p css={{ ...theme.text.small, color: theme.fontColor.muted }}>
          No matching categories
        </p>
      )}
    </List>
  )
}

CategoryMenu.propTypes = {
  items: PropTypes.array,
  refine: PropTypes.func,
  createURL: PropTypes.func,
}

export const CustomCategoryMenu = connectMenu(CategoryMenu)

const AuthorMenu = ({ items, refine, createURL }) => {
  const theme = useTheme()

  const authors = useAuthor()

  return (
    <List space="base">
      {items.length > 0 ? (
        items.map((item) => {
          const authorMetadata = getAuthor(authors, item.label)

          return (
            <ListItem key={item.value}>
              <a
                href={createURL(item.value)}
                onClick={(event) => {
                  event.preventDefault()
                  refine(item.value)
                }}
                css={[
                  link.reset(theme),
                  {
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr',
                    gap: theme.space.small,
                    alignItems: 'center',
                    position: 'relative',

                    '&:hover, &:focus': {
                      img: {
                        transform: 'scale(1.05)',
                      },
                    },
                  },

                  item.isRefined && {
                    '&::after': {
                      content: "''",
                      width: StylesAlgoliaVariables.notificationSize,
                      height: StylesAlgoliaVariables.notificationSize,
                      position: 'absolute',
                      background: theme.color.info.base,
                      borderRadius: theme.borderRadius.rounded,
                      zIndex: 2,
                      top: 0,
                      left: -StylesAlgoliaVariables.notificationSize / 4,
                      border: `2px solid ${theme.color.light.base}`,
                    },
                  },
                ]}
              >
                <Image
                  css={{
                    transition: `transform ${theme.timing.base} ${theme.transition.bounce}`,
                    transformOrigin: 'center center',
                    width: StylesAvatarVariables(theme).size.base,
                  }}
                  src={authorMetadata.frontmatter.photo}
                  alt=""
                />
                <div>
                  <div
                    css={{
                      ...theme.text.small,
                      fontWeight: theme.fontWeight.bold,
                    }}
                  >
                    {item.label}
                  </div>
                  <div
                    css={{
                      ...theme.text.xsmall,
                      marginTop: theme.space.xsmall,
                      color: theme.fontColor.muted,
                    }}
                  >
                    {item.count} article{item.count > 1 && 's'}
                  </div>
                </div>
              </a>
            </ListItem>
          )
        })
      ) : (
        <p css={{ ...theme.text.small, color: theme.fontColor.muted }}>
          No matching authors
        </p>
      )}
    </List>
  )
}

AuthorMenu.propTypes = {
  items: PropTypes.array,
  refine: PropTypes.func,
  createURL: PropTypes.func,
}

export const CustomAuthorMenu = connectMenu(AuthorMenu)

export const Hit = ({ hit }) => (
  <TutorialTeaser
    link={`${hit.slug}${hit.contentId ? `#${hit.contentId}` : ''}`}
    title={<Highlight tagName="mark" attribute="title" hit={hit} />}
    author={hit.author}
    excerpt={<Highlight tagName="mark" attribute="excerpt" hit={hit} />}
    categories={hit.categories}
  />
)

Hit.propTypes = {
  hit: PropTypes.object,
}
